"use client"

import * as React from "react"
import { Drawer as DrawerPrimitive } from "vaul"

import { cn } from "../lib/utils"

function Drawer({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) {
  return <DrawerPrimitive.Root data-slot="drawer" {...props} />
}

function DrawerTrigger({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />
}

function DrawerPortal({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />
}

function DrawerClose({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />
}

/**
 * Hidden by default (the old `bg-black/50` scrim read as a takeover behind a
 * docked panel). Pass `blockOutside` on `DrawerContent` to render it instead —
 * see that prop for when you need to.
 */
function DrawerOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
  return (
    <DrawerPrimitive.Overlay
      data-slot="drawer-overlay"
      className={cn("hidden", className)}
      {...props}
    />
  )
}

/**
 * `blockOutside` renders the otherwise-hidden overlay as a LIGHT scrim
 * (matching `SheetOverlay`), whose real job is to absorb clicks aimed at the
 * page behind the drawer.
 *
 * Only needed by a drawer that ALSO preventDefault()s
 * onPointerDownOutside/onInteractOutside to stop a stray click discarding a
 * half-filled form. Radix sets `body { pointer-events: none }` only for the
 * topmost dismissable layer, so such a drawer stays open while the click still
 * lands on whatever is underneath — which is how clicking a calendar block
 * behind the open "Schedule Slot" drawer opened a SECOND right-hand panel on
 * top of it. Dimming is the visual job; blocking is the structural one.
 *
 * Leave it off for a drawer that closes on outside click: there the click is
 * consumed by the close, so nothing leaks through.
 */
function DrawerContent({
  className,
  children,
  style,
  blockOutside = false,
  overlayClassName,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Content> & {
  blockOutside?: boolean
  /** Extra classes for the `blockOutside` scrim, e.g. a heavier tint or a
   *  backdrop blur when the drawer must read as "answer this first". */
  overlayClassName?: string
}) {
  // Reserve iOS safe-area space. Bottom drawers only need bottom inset,
  // top drawers only top, and side drawers need both. Resolves to 0px on web.
  // We rely on data-vaul-drawer-direction via CSS vars on the element,
  // but since style is static here we apply both and let direction-specific
  // padding be additive — bottom: pb only matters since pt is 0 for bottom anyway.
  // Cleaner: apply via CSS using the data attribute.
  return (
    <DrawerPortal data-slot="drawer-portal">
      <DrawerOverlay
        className={
          blockOutside
            ? cn(
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:duration-200 data-[state=closed]:duration-150 motion-reduce:animate-none fixed inset-0 !z-[9998] block bg-black/20 dark:bg-black/50",
                overlayClassName
              )
            : undefined
        }
      />
      <DrawerPrimitive.Content
        data-slot="drawer-content"
        className={cn(
          "bg-background group/drawer-content fixed !z-[9999] flex h-auto flex-col",
          "data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-lg data-[vaul-drawer-direction=top]:border-b data-[vaul-drawer-direction=top]:pt-[env(safe-area-inset-top,0px)]",
          "data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh] data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=bottom]:border-t data-[vaul-drawer-direction=bottom]:pb-[env(safe-area-inset-bottom,0px)]",
          // Use physical right/left (not logical end/start) so vaul's translateX transforms
          // align correctly in both LTR and RTL page directions
          "data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=right]:sm:max-w-sm data-[vaul-drawer-direction=right]:pt-[env(safe-area-inset-top,0px)] data-[vaul-drawer-direction=right]:pb-[env(safe-area-inset-bottom,0px)]",
          "data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:border-r data-[vaul-drawer-direction=left]:sm:max-w-sm data-[vaul-drawer-direction=left]:pt-[env(safe-area-inset-top,0px)] data-[vaul-drawer-direction=left]:pb-[env(safe-area-inset-bottom,0px)]",
          className
        )}
        style={style}
        {...props}
      >
        <div className="bg-muted mx-auto mt-4 hidden h-2 w-[100px] shrink-0 rounded-full group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
}

function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  )
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  )
}

function DrawerTitle({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  )
}

function DrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
